import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QuickModeTab from './QuickModeTab';
import DeepModeTab from './DeepModeTab';
import ModeToggle from './ModeToggle';
import { localizedValue } from '../../localization/presentationLanguage';

const synText = (es, en) => localizedValue({ es, en });

const MODE_LABELS = {
  quick: () => synText('Modo activo · Compatibilidad Solar Rápida', 'Active mode · Quick Solar Compatibility'),
  deep: () => synText('Modo activo · Sinastría Profunda Premium', 'Active mode · Premium Deep Synastry'),
};

const styles = {
  page: {
    minHeight: '100vh',
    background: [
      'radial-gradient(circle at 12% 10%, rgba(218,165,32,0.16), transparent 20%)',
      'radial-gradient(circle at 88% 8%, rgba(138,43,226,0.16), transparent 20%)',
      'radial-gradient(circle at 50% 42%, rgba(255,255,255,0.035), transparent 28%)',
      'linear-gradient(180deg, #12051d 0%, #1a0b2d 42%, #0a0612 100%)',
    ].join(', '),
    color: '#F5E7B2',
    padding: '14px 14px 60px',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'visible',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorY: 'auto',
    touchAction: 'pan-y',
  },
  deepMobilePage: {
    minHeight: '100dvh',
    overflowX: 'hidden',
    overflowY: 'visible',
    padding: 0,
    paddingTop: 0,
    touchAction: 'pan-y',
    overscrollBehaviorY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  deepMobileScroller: {
    minHeight: '100dvh',
    overflow: 'visible',
    boxSizing: 'border-box',
    padding: 'calc(env(safe-area-inset-top, 0px) + 112px) 12px calc(env(safe-area-inset-bottom, 0px) + 34px)',
  },
  starfield: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    touchAction: 'pan-y',
    backgroundImage:
      'radial-gradient(circle, rgba(255,255,255,0.85) 0 1px, transparent 1.2px), radial-gradient(circle, rgba(255,255,255,0.30) 0 1px, transparent 1.4px)',
    backgroundSize: '120px 120px, 180px 180px',
    backgroundPosition: '0 0, 40px 65px',
    opacity: 0.18,
    mixBlendMode: 'screen',
  },
  ambientOrbA: {
    position: 'fixed',
    top: -80,
    left: -70,
    width: 240,
    height: 240,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(218,165,32,0.20) 0%, rgba(218,165,32,0.04) 52%, transparent 74%)',
    filter: 'blur(10px)',
    pointerEvents: 'none',
    touchAction: 'pan-y',
  },
  ambientOrbB: {
    position: 'fixed',
    right: -90,
    top: 20,
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(138,43,226,0.22) 0%, rgba(138,43,226,0.05) 50%, transparent 74%)',
    filter: 'blur(12px)',
    pointerEvents: 'none',
    touchAction: 'pan-y',
  },
  shell: {
    maxWidth: 1160,
    margin: '0 auto',
    position: 'relative',
    paddingTop: 104,
    zIndex: 2,
  },
  deepShell: {
    maxWidth: 1160,
    margin: '0 auto',
    position: 'relative',
    paddingTop: 104,
    zIndex: 2,
  },
  topBar: {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
    left: 0,
    right: 0,
    width: '100%',
    maxWidth: 1160,
    margin: '0 auto',
    padding: '0 18px',
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 14,
    zIndex: 999,
    pointerEvents: 'none',
    boxSizing: 'border-box',
  },
  topBarMobile: {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + 4px)',
    left: 0,
    right: 0,
    padding: '0 14px',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: '"back toggle" "badge badge"',
    alignItems: 'center',
    gap: '6px 10px',
    zIndex: 999,
    pointerEvents: 'none',
    boxSizing: 'border-box',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    minHeight: 46,
    borderRadius: 999,
    padding: '0 18px 0 12px',
    background: 'linear-gradient(180deg, rgba(18,5,42,0.90), rgba(8,2,24,0.96))',
    border: '1px solid rgba(232,95,255,0.62)',
    color: '#FFE08B',
    fontWeight: 900,
    fontSize: 14,
    letterSpacing: 0.2,
    cursor: 'pointer',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    boxShadow: '0 0 24px rgba(190,60,255,0.28), 0 12px 30px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.08)',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    userSelect: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, filter 0.18s ease',
    zIndex: 1001,
    pointerEvents: 'auto',
    justifySelf: 'start',
  },
  backButtonMobile: {
    gridArea: 'back',
    width: 'max-content',
    maxWidth: '100%',
    minHeight: 42,
    padding: '0 15px 0 10px',
    justifySelf: 'start',
    alignSelf: 'center',
  },
  backIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(circle at 35% 24%, rgba(255,224,130,0.24), rgba(160,68,255,0.12) 68%, rgba(5,1,18,0.34) 100%)',
    border: '1px solid rgba(255,218,116,0.42)',
    color: '#FFE98A',
    flex: '0 0 auto',
    boxShadow: '0 0 16px rgba(218,165,32,0.20), inset 0 0 10px rgba(255,218,116,0.08)',
  },
  backLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
  },
  topCenterBadge: {
    justifySelf: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(218,165,32,0.18)',
    color: 'rgba(255,243,196,0.88)',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.45,
    textTransform: 'uppercase',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    
    pointerEvents: 'auto',
  },
  topCenterBadgeMobile: {
    gridArea: 'badge',
    width: '100%',
    justifySelf: 'stretch',
    justifyContent: 'center',
    padding: '5px 10px',
    fontSize: 10.5,
    letterSpacing: 0.35,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  toggleWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    justifySelf: 'end',
    pointerEvents: 'auto',
    minWidth: 0,
  },
  toggleWrapMobile: {
    gridArea: 'toggle',
    justifyContent: 'flex-end',
    justifySelf: 'end',
    maxWidth: '100%',
    minWidth: 0,
  },
  heroCard: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 30,
    padding: '34px 28px 30px',
    border: '1px solid rgba(218,165,32,0.20)',
    background:
      'linear-gradient(135deg, rgba(70,10,124,0.94) 0%, rgba(26,11,52,0.96) 46%, rgba(11,7,24,0.985) 100%)',
    boxShadow: '0 28px 70px rgba(0,0,0,0.38), 0 0 0 1px rgba(255,255,255,0.03) inset',
    marginBottom: 20,
    marginTop: 0,
  },
  heroGlowGold: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(218,165,32,0.22) 0%, rgba(218,165,32,0.06) 42%, transparent 74%)',
    filter: 'blur(10px)',
    top: -86,
    right: -54,
    pointerEvents: 'none',
  },
  heroGlowViolet: {
    position: 'absolute',
    width: 220,
    height: 220,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(138,43,226,0.18) 0%, rgba(138,43,226,0.03) 48%, transparent 74%)',
    filter: 'blur(10px)',
    bottom: -90,
    left: -44,
    pointerEvents: 'none',
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.45fr) minmax(280px, 0.9fr)',
    gap: 22,
    alignItems: 'stretch',
  },
  heroGridMobile: {
    gridTemplateColumns: '1fr',
  },
  eyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 12px',
    borderRadius: 999,
    background: 'rgba(218,165,32,0.10)',
    border: '1px solid rgba(218,165,32,0.20)',
    color: '#F7D774',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 14,
  },
  title: {
    margin: 0,
    fontSize: 'clamp(30px, 4.1vw, 46px)',
    lineHeight: 1.02,
    color: '#FFF3C4',
    fontWeight: 900,
    letterSpacing: -0.6,
  },
  titleAccent: {
    display: 'block',
    marginTop: 6,
    color: '#FFD870',
    textShadow: '0 0 18px rgba(218,165,32,0.18)',
  },
  subtitle: {
    margin: '14px 0 0',
    color: 'rgba(255,243,196,0.84)',
    maxWidth: 720,
    fontSize: 15,
    lineHeight: 1.72,
  },
  heroMetaRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 18,
  },
  heroChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 13px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.045)',
    border: '1px solid rgba(218,165,32,0.14)',
    color: 'rgba(255,243,196,0.88)',
    fontSize: 12,
    fontWeight: 800,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  },
  heroAside: {
    borderRadius: 24,
    padding: '18px 18px 16px',
    border: '1px solid rgba(218,165,32,0.16)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.03))',
    boxShadow: '0 16px 36px rgba(0,0,0,0.18)',
    display: 'grid',
    gap: 14,
    alignContent: 'start',
  },
  asideTitle: {
    margin: 0,
    color: '#FFF0B8',
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.2,
  },
  asideCopy: {
    margin: 0,
    color: 'rgba(255,243,196,0.76)',
    fontSize: 13,
    lineHeight: 1.62,
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
  },
  statCard: {
    borderRadius: 18,
    padding: '14px 12px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(218,165,32,0.12)',
  },
  statLabel: {
    display: 'block',
    color: 'rgba(255,243,196,0.68)',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.38,
    marginBottom: 6,
  },
  statValue: {
    display: 'block',
    color: '#FFE89B',
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.1,
  },
  statSub: {
    display: 'block',
    marginTop: 4,
    color: 'rgba(255,243,196,0.70)',
    fontSize: 12,
    lineHeight: 1.35,
  },
  surfaceCard: {
    borderRadius: 22,
    padding: 16,
    border: '1px solid rgba(218,165,32,0.12)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.032), rgba(255,255,255,0.022))',
    boxShadow: '0 14px 34px rgba(0,0,0,0.14)',
    marginBottom: 18,
  },
  surfaceGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(280px, 0.8fr)',
    gap: 18,
    alignItems: 'center',
  },
  surfaceGridMobile: {
    gridTemplateColumns: '1fr',
  },
  surfaceHeading: {
    margin: 0,
    color: '#FFF0B8',
    fontSize: 16,
    fontWeight: 900,
  },
  sectionHint: {
    margin: '8px 0 0',
    color: 'rgba(255,243,196,0.72)',
    fontSize: 13,
    lineHeight: 1.62,
  },
  quickInfoWrap: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
  },
  quickInfoWrapMobile: {
    gridTemplateColumns: '1fr',
  },
  quickInfoCard: {
    borderRadius: 18,
    padding: '14px 12px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(218,165,32,0.12)',
  },
  quickInfoTitle: {
    margin: 0,
    color: '#F7D774',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.42,
  },
  quickInfoText: {
    margin: '7px 0 0',
    color: 'rgba(255,243,196,0.76)',
    fontSize: 12,
    lineHeight: 1.55,
  },
  modeShell: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr)',
    gap: 20,
  },
};

function inferSavedReadingMode(savedReading) {
  const mode = String(
    savedReading?.mode ||
    savedReading?.readingType ||
    savedReading?.calculationMode ||
    savedReading?.sourceMode ||
    ''
  )
    .trim()
    .toLowerCase();

  if (mode === 'quick' || mode === 'deep') return mode;

  const source = String(
    savedReading?.source ||
    savedReading?.sourceType ||
    savedReading?.raw?.source ||
    savedReading?.raw?.sourceType ||
    ''
  )
    .trim()
    .toLowerCase();

  if (source.includes('quick') || source.includes('solar')) return 'quick';
  if (source.includes('deep') || source.includes('synastry')) return 'deep';

  const payload =
    savedReading?.resultSnapshot ||
    savedReading?.result ||
    savedReading?.reading ||
    savedReading?.raw?.resultSnapshot ||
    savedReading?.raw?.result ||
    savedReading?.raw?.reading ||
    savedReading ||
    null;

  const dims = payload?.compatibility?.dimensions || payload?.dimensions || {};

  const looksQuick = Boolean(
    payload?.pair?.signA && payload?.pair?.signB
  ) || Boolean(
    dims &&
      typeof dims === 'object' &&
      ('energy' in dims || 'expression' in dims || 'magnetism' in dims || 'growth' in dims)
  );

  const looksDeep = Boolean(
    payload?.metadata?.personA ||
    payload?.metadata?.personB ||
    payload?.charts?.personA ||
    payload?.charts?.personB ||
    payload?.houseOverlays ||
    payload?.significantAspects ||
    (dims &&
      typeof dims === 'object' &&
      ('attraction' in dims || 'emotional' in dims || 'communication' in dims || 'karmic' in dims))
  );

  if (looksQuick && !looksDeep) return 'quick';
  if (looksDeep) return 'deep';

  return 'quick';
}

const HeroCard = memo(function HeroCard({ isMobile }) {
  return (
    <div style={styles.heroCard}>
      <div style={styles.heroGlowGold} />
      <div style={styles.heroGlowViolet} />

      <div
        style={{
          ...styles.heroGrid,
          ...(isMobile ? styles.heroGridMobile : null),
        }}
      >
        <div>
          <div style={styles.eyebrow}>{synText('✦ Sala de Sinastría · Compatibilidad Solar & Profunda', '✦ Synastry Room · Solar & Deep Compatibility')}</div>
          <h1 style={styles.title}>
            {synText('Descubrí la sinergia cósmica', 'Discover the cosmic synergy')}
            <span style={styles.titleAccent}>{synText('entre dos personas', 'between two people')}</span>
          </h1>
          <p style={styles.subtitle}>
            {synText('Explorá la compatibilidad solar para una lectura rápida o pasá a la sinastría profunda para analizar Luna, Ascendente, atracción, estabilidad, cruces de casas y dinámica emocional con una experiencia premium pensada para Zodiacus.', 'Explore solar compatibility for a quick reading, or switch to deep synastry to analyze the Moon, Ascendant, attraction, stability, house overlays, and emotional dynamics in a premium Zodiacus experience.')}
          </p>

          <div style={styles.heroMetaRow}>
            <span style={styles.heroChip}>{synText('💞 Parejas & vínculos', '💞 Couples & connections')}</span>
            <span style={styles.heroChip}>{synText('☀ Modo rápido instantáneo', '☀ Instant quick mode')}</span>
            <span style={styles.heroChip}>{synText('✦ Modo profundo con Swiss exacto', '✦ Deep mode with precise Swiss Ephemeris data')}</span>
          </div>
        </div>

        <div style={styles.heroAside}>
          <h3 style={styles.asideTitle}>{synText('Experiencia premium de lectura', 'Premium reading experience')}</h3>
          <p style={styles.asideCopy}>
            {synText('Compará dos cartas desde una mirada rápida o profunda con una interfaz diseñada para destacar química, estabilidad, resonancia emocional y profundidad kármica en un entorno astrológico premium.', 'Compare two charts through a quick or deep reading designed to highlight chemistry, stability, emotional resonance, and karmic depth.')}
          </p>

          <div style={styles.statGrid}>
            <div style={styles.statCard}>
              <span style={styles.statLabel}>{synText('Modo profundo', 'Deep mode')}</span>
              <span style={styles.statValue}>{synText('9 puntos', '9 points')}</span>
              <span style={styles.statSub}>{synText('Sol, Luna, Mercurio, Venus, Marte, Júpiter, Saturno, Nodo Norte y Ascendente.', 'Sun, Moon, Mercury, Venus, Mars, Jupiter, Saturn, North Node, and Ascendant.')}</span>
            </div>
            <div style={styles.statCard}>
              
              <span style={styles.statSub}>{synText('Cruces de casas, highlights narrativos y lectura relacional más rica.', 'House overlays, narrative highlights, and a richer relationship reading.')}</span>
            </div>
            <div style={styles.statCard}>
              <span style={styles.statLabel}>{synText('Rápido', 'Quick')}</span>
              <span style={styles.statValue}>Solar</span>
              <span style={styles.statSub}>{synText('Ideal para explorar compatibilidad solar de forma ágil y visual.', 'Ideal for exploring solar compatibility quickly and visually.')}</span>
            </div>
            <div style={styles.statCard}>
              <span style={styles.statLabel}>{synText('Estética', 'Design')}</span>
              <span style={styles.statValue}>Gold · Violet</span>
              <span style={styles.statSub}>{synText('Glassmorphism cósmico con lectura clara en mobile y desktop.', 'Cosmic glassmorphism with clear reading on mobile and desktop.')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const IntroSurface = memo(function IntroSurface({ isMobile }) {
  return (
    <div style={styles.surfaceCard}>
      <div
        style={{
          ...styles.surfaceGrid,
          ...(isMobile ? styles.surfaceGridMobile : null),
        }}
      >
        <div>
          <h2 style={styles.surfaceHeading}>{synText('Elegí cómo querés explorar la compatibilidad', 'Choose how you want to explore compatibility')}</h2>
          <p style={styles.sectionHint}>
            {synText('Entrá por Solar si buscás una primera lectura inmediata por signos solares, o usá Modo Profundo para desbloquear una experiencia más seria con fecha, hora, lugar y capas interpretativas mucho más completas.', 'Choose Solar for an immediate sun-sign reading, or use Deep Mode for a more detailed experience with date, time, place, and richer interpretation layers.')}
          </p>
        </div>

        <div
          style={{
            ...styles.quickInfoWrap,
            ...(isMobile ? styles.quickInfoWrapMobile : null),
          }}
        >
          <div style={styles.quickInfoCard}>
            <h3 style={styles.quickInfoTitle}>{synText('Modo rápido', 'Quick mode')}</h3>
            <p style={styles.quickInfoText}>{synText('Compatibilidad solar rápida, ideal para explorar energía base, dinámica elemental y primera afinidad.', 'Quick solar compatibility for exploring core energy, elemental dynamics, and initial affinity.')}</p>
          </div>
          <div style={styles.quickInfoCard}>
            <h3 style={styles.quickInfoTitle}>{synText('Modo profundo', 'Deep mode')}</h3>
            <p style={styles.quickInfoText}>{synText('Lectura profunda con Luna, Ascendente, dimensiones, aspectos dominantes y narrativa de vínculo premium.', 'A deep reading with the Moon, Ascendant, dimensions, dominant aspects, and a premium relationship narrative.')}</p>
          </div>
          <div style={styles.quickInfoCard}>
            <h3 style={styles.quickInfoTitle}>{synText('Cruces de casas', 'House overlays')}</h3>
            <p style={styles.quickInfoText}>{synText('Descubrí cómo los planetas de una persona caen en las casas de la otra para leer intimidad, atracción y resonancia real.', 'Discover how one person’s planets fall in the other person’s houses to understand intimacy, attraction, and genuine resonance.')}</p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default function SynastryRoomScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [reopenSavedSynastry, setReopenSavedSynastry] = useState(
    () => location?.state?.reopenSavedSynastry || null
  );

  const [reopenSavedReadingMode] = useState(() =>
    inferSavedReadingMode(location?.state?.reopenSavedSynastry || null)
  );
  const requestedMode = useMemo(() => {
    if (
      location?.state?.openDeepMode ||
      location?.state?.initialTab === 'deep' ||
      location?.state?.mode === 'deep' ||
      reopenSavedReadingMode === 'deep'
    ) {
      return 'deep';
    }

    if (
      location?.state?.mode === 'quick' ||
      location?.state?.initialTab === 'quick' ||
      reopenSavedReadingMode === 'quick'
    ) {
      return 'quick';
    }

    return 'quick';
  }, [location?.state, reopenSavedReadingMode]);

  const [mode, setMode] = useState(requestedMode);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    if (!reopenSavedSynastry) return;

    const currentState =
      location?.state && typeof location.state === 'object'
        ? location.state
        : {};

    const {
      reopenSavedSynastry: _consumedSavedSynastry,
      ...remainingState
    } = currentState;

    setReopenSavedSynastry(null);

    navigate(
      `${location.pathname}${location.search || ''}${location.hash || ''}`,
      {
        replace: true,
        state: Object.keys(remainingState).length
          ? remainingState
          : null,
      }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    reopenSavedSynastry,
  ]);

  useEffect(() => {
    setMode(requestedMode);
  }, [requestedMode]);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 768);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleModeChange = useCallback((nextMode) => {
    if (nextMode !== 'quick' && nextMode !== 'deep') return;
    setMode(nextMode);
  }, []);

  const isDeepMobile = isMobile && mode === 'deep';
  const isDeepMode = mode === 'deep';

  const modeLabel = (MODE_LABELS[mode] || MODE_LABELS.quick)();

  return (
    <div style={isDeepMobile ? { ...styles.page, ...styles.deepMobilePage } : styles.page}>
      {!isDeepMobile ? <div style={styles.starfield} aria-hidden="true" /> : null}
      {!isDeepMobile ? <div style={styles.ambientOrbA} aria-hidden="true" /> : null}
      {!isDeepMobile ? <div style={styles.ambientOrbB} aria-hidden="true" /> : null}

      <div
        style={
          isDeepMobile
            ? { ...styles.deepShell, paddingTop: 0, height: '100%', minHeight: 0 }
            : isDeepMode
              ? styles.deepShell
              : styles.shell
        }
      >
        <div style={isMobile ? styles.topBarMobile : styles.topBar}>
          <button
            type="button"
            onClick={() => navigate('/home', { replace: true })}
            aria-label={synText('Volver al Home', 'Back to Home')}
            style={{
              ...styles.backButton,
              ...(isMobile ? styles.backButtonMobile : null),
            }}
          >
            <span style={styles.backIconWrap} aria-hidden="true">←</span>
            <span style={styles.backLabel}>Home</span>
          </button>

          <div
            style={{
              ...styles.topCenterBadge,
              ...(isMobile ? styles.topCenterBadgeMobile : null),
            }}
          >
            ✦ {modeLabel}
          </div>

          <div
            style={{
              ...styles.toggleWrap,
              ...(isMobile ? styles.toggleWrapMobile : null),
            }}
          >
            <ModeToggle mode={mode} onChange={handleModeChange} />
          </div>
        </div>

        {!isDeepMode ? <HeroCard isMobile={isMobile} /> : null}
        {!isDeepMode ? <IntroSurface isMobile={isMobile} /> : null}

        {isDeepMobile ? (
          <div id="synastryDeepMobileScroller" style={styles.deepMobileScroller}>
            <DeepModeTab initialSavedReading={reopenSavedReadingMode === 'deep' ? reopenSavedSynastry : null} />
          </div>
        ) : (
          <div style={styles.modeShell}>
            {mode === 'quick' && (
              <QuickModeTab initialSavedReading={reopenSavedReadingMode === 'quick' ? reopenSavedSynastry : null} />
            )}
            {mode === 'deep' && (
              <DeepModeTab initialSavedReading={reopenSavedReadingMode === 'deep' ? reopenSavedSynastry : null} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
